import {useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {MdClose} from 'react-icons/md'
import {fetchAuthSession} from 'aws-amplify/auth'

interface DiagnosticEmailModalProps {
    isOpen: boolean
    onClose: () => void
    onSend: (data: DiagnosticEmailData) => void
    isLoading?: boolean
}

export interface DiagnosticEmailData {
    type: 'security' | 'request' | 'permission'
    appVariant: 'lucent-health' | 'communicare'
    recipientEmail: string
    theme?: 'light' | 'dark'
}

export function DiagnosticEmailModal({
    isOpen,
    onClose,
    onSend,
    isLoading = false
}: DiagnosticEmailModalProps) {
    const modalRef = useRef<HTMLDivElement>(null)
    const [type, setType] = useState<'security' | 'request' | 'permission'>('security')
    const [appVariant, setAppVariant] = useState<'lucent-health' | 'communicare'>('lucent-health')
    const [recipientEmail, setRecipientEmail] = useState('')
    const [theme, setTheme] = useState<'light' | 'dark' | undefined>('light')

    useEffect(() => {
        if (isOpen) {
            const loadUserEmail = async () => {
                try {
                    const session = await fetchAuthSession()
                    const idToken = session.tokens?.idToken
                    if (idToken?.payload?.email) {
                        setRecipientEmail(idToken.payload.email as string)
                    }
                } catch (error) {
                    console.error('Error loading user email:', error)
                }
            }
            loadUserEmail()
        }
    }, [isOpen])

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen && !isLoading) {
                onClose()
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleEscape)
            modalRef.current?.focus()
        }

        return () => {
            document.removeEventListener('keydown', handleEscape)
        }
    }, [isOpen, onClose, isLoading])

    if (!isOpen) return null

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!isLoading && recipientEmail) {
            onSend({
                type,
                appVariant,
                recipientEmail,
                theme: theme || undefined
            })
        }
    }

    const modal = (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
        >
            <div
                className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={!isLoading ? onClose : undefined}
            />

            <div
                ref={modalRef}
                className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all"
                tabIndex={-1}
            >
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h3 id="modal-title" className="text-lg font-semibold text-gray-900">
                        Send Diagnostic Email
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                        <MdClose size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="p-6 space-y-4">
                        <div>
                            <label htmlFor="email-type" className="block text-sm font-medium text-gray-700 mb-1">
                                Email Type
                            </label>
                            <select
                                id="email-type"
                                value={type}
                                onChange={(e) => setType(e.target.value as 'security' | 'request' | 'permission')}
                                disabled={isLoading}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="security">Security</option>
                                <option value="request">Request</option>
                                <option value="permission">Permission</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="app-variant" className="block text-sm font-medium text-gray-700 mb-1">
                                Organization
                            </label>
                            <select
                                id="app-variant"
                                value={appVariant}
                                onChange={(e) => setAppVariant(e.target.value as 'lucent-health' | 'communicare')}
                                disabled={isLoading}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="lucent-health">Lucent Health</option>
                                <option value="communicare">My Health 365</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="theme" className="block text-sm font-medium text-gray-700 mb-1">
                                Theme
                            </label>
                            <select
                                id="theme"
                                value={theme || ''}
                                onChange={(e) => setTheme(e.target.value ? e.target.value as 'light' | 'dark' : undefined)}
                                disabled={isLoading}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Default</option>
                                <option value="light">Light</option>
                                <option value="dark">Dark</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="recipient-email" className="block text-sm font-medium text-gray-700 mb-1">
                                Recipient Email
                            </label>
                            <input
                                id="recipient-email"
                                type="email"
                                value={recipientEmail}
                                onChange={(e) => setRecipientEmail(e.target.value)}
                                disabled={isLoading}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter recipient email"
                            />
                        </div>
                    </div>

                    <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-lg">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isLoading}
                            className={`px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${
                                isLoading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading || !recipientEmail}
                            className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                                isLoading || !recipientEmail ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                            {isLoading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg"
                                         fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor"
                                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Sending...
                                </span>
                            ) : (
                                'Send Email'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )

    return createPortal(modal, document.body)
}
